/**
 * StrategyForkDialog — Phase V (V4) "fork on fresh inputs" flow.
 *
 * Collects the successor name and the REQUIRED hypothesis (D28 — the UI teaches the
 * experiment discipline), shows what the fork will pin, and reminds that the
 * original strategy is never modified. Single dismiss affordance: SDialog header X.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, Stack, TextField, Typography } from '@mui/material';
import { SDialog } from '@modules/ui-components';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import type { CliStrategyStaleness } from '@modules/shared-types';

export interface StrategyForkDialogProps {
  open: boolean;
  predecessorName: string;
  verdict: CliStrategyStaleness | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: { name: string; forkHypothesis: string }) => void;
  /**
   * F-LIVE2-44: the fork's own failure, stated HERE. Until fix wave 9 both the
   * completion notice and the `IMMUTABLE_PREDECESSOR_REQUIRED` refusal arrived
   * as raw browser `alert()`s — the third sighting of the channel F-LIVE2-23a
   * and F-LIVE2-28 already flagged. The raw server message travels as-is; the
   * dialog owns the human sentence.
   */
  error?: string | null;
}

const shortId = (id?: string): string => (id ? `${id.slice(0, 6)}…` : '?');

/** The refusal an operator can actually act on, plus the code as a footnote. */
const IMMUTABLE_PREDECESSOR_CODE = 'IMMUTABLE_PREDECESSOR_REQUIRED';

/**
 * The server's refusals are `CODE: sentence` (`strategyForkLineage.ts`,
 * `strategyInputManifest.ts`), so the code is matched as the PREFIX it is —
 * fix round 1, review Minor #6. `includes` rewrote any message that merely
 * quoted the code into the bake sentence, and one such message already exists:
 * `BlockCanvasEditor` prefixes a non-refusal failure with «The strategy was
 * created but generation did not start: …».
 */
const isImmutablePredecessorRefusal = (error: string): boolean => {
  const trimmed = error.trimStart();
  if (!trimmed.startsWith(IMMUTABLE_PREDECESSOR_CODE)) return false;
  // A longer code that merely starts with these words is a different refusal.
  const rest = trimmed.slice(IMMUTABLE_PREDECESSOR_CODE.length);
  return rest.length === 0 || /^[\s:.-]/.test(rest);
};

function forkErrorCopy(error: string): { sentence: string; code?: string } {
  if (isImmutablePredecessorRefusal(error)) {
    return {
      sentence: 'This strategy must be baked before it can be forked — bake a version first.',
      code: error,
    };
  }
  return { sentence: error };
}

export const StrategyForkDialog: React.FC<StrategyForkDialogProps> = ({
  open, predecessorName, verdict, submitting, onClose, onSubmit, error = null,
}) => {
  const [name, setName] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(`${predecessorName} (fork)`);
      setHypothesis('');
      setTouched(false);
    }
  }, [open, predecessorName]);

  const hypothesisError = touched && hypothesis.trim().length === 0;

  /** One reading of the error per render — the sentence and its caption agree. */
  const errorCopy = useMemo(() => (error ? forkErrorCopy(error) : null), [error]);

  const primaryAction = useMemo(() => ({
    key: 'primary',
    label: submitting ? 'Creating fork…' : 'Create fork',
    onClick: () => {
      setTouched(true);
      if (!hypothesis.trim() || !name.trim()) return;
      onSubmit({ name: name.trim(), forkHypothesis: hypothesis.trim() });
    },
    disabled: submitting,
  }), [submitting, name, hypothesis, onSubmit]);

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title="Fork on fresh inputs"
      primaryAction={primaryAction}
      fullWidth
      maxWidth="sm"
      data-testid={STRATEGY_CANVAS.forkDialog}
    >
      <Stack spacing={2}>
        {/* F-LIVE2-44: the refusal sits above what it invalidates and says what
            the operator can DO; the raw code is a secondary caption, not the
            message. The dialog stays open so the fix is one step away. */}
        {errorCopy && (
          <Alert severity="error" role="alert" data-testid={STRATEGY_CANVAS.forkError}>
            <AlertTitle sx={{ mb: 0.5 }}>The fork was not created</AlertTitle>
            <Typography variant="body2">{errorCopy.sentence}</Typography>
            {errorCopy.code && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}
                data-testid={STRATEGY_CANVAS.forkErrorCode}
              >
                {errorCopy.code}
              </Typography>
            )}
          </Alert>
        )}
        <TextField
          label="New strategy name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'fork-name-input' }}
        />
        <TextField
          label="Hypothesis"
          required
          multiline
          minRows={3}
          value={hypothesis}
          onChange={(event) => setHypothesis(event.target.value)}
          onBlur={() => setTouched(true)}
          InputLabelProps={{ shrink: true }}
          error={hypothesisError}
          helperText={hypothesisError
            ? 'Hypothesis is required — it becomes part of the experiment history.'
            : 'Why fork? What do you expect the fresh inputs to change? Recorded in the experiment history (D28).'}
          fullWidth
          inputProps={{ 'data-testid': 'fork-hypothesis-input' }}
        />
        {verdict && (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Typography variant="overline" color="text.secondary">What the fork will pin</Typography>
            {verdict.insightsAnalysis.drifted && (
              <Typography variant="body2">
                Insights analysis → {shortId(verdict.insightsAnalysis.latestCompleted)}{' '}
                (was {shortId(verdict.insightsAnalysis.pinned)})
              </Typography>
            )}
            {verdict.variableSet.drifted && (
              <Typography variant="body2">
                Variable set → v{verdict.variableSet.current} (was v{verdict.variableSet.pinned})
              </Typography>
            )}
            {verdict.tasks.drifted && (
              <Typography variant="body2">
                Tasks → current set ({verdict.tasks.changed.length} changed, {verdict.tasks.deleted.length} deleted vs pin)
              </Typography>
            )}
            {!verdict.stale && (
              <Typography variant="body2" color="text.secondary">Inputs match the pin — fork will re-pin the same snapshot.</Typography>
            )}
          </Box>
        )}
        <Alert severity="info">
          The original strategy is <b>not modified</b>. The fork is a new strategy generated from
          the fresh snapshot, linked to the earlier version for comparison.
        </Alert>
      </Stack>
    </SDialog>
  );
};
